import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export default function TablesAdmin() {
  const [tables, setTables]   = useState([]);
  const [editTable, setEditTable] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState('');
  const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');

  const load = useCallback(async () => {
    const data = await api.getTables();
    setTables(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(''), 2500); }

  async function saveTable(form) {
    setSaving(true);
    try {
      if (form.id) {
        await fetch(`/api/tables/${form.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ number: parseInt(form.number), name: form.name, seats: parseInt(form.seats) }),
        });
        showToast('Zapisano zmiany');
      } else {
        const res = await fetch('/api/tables', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ number: parseInt(form.number), name: form.name, seats: parseInt(form.seats) }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        showToast(`Dodano stolik ${form.number}`);
      }
      setEditTable(null);
      load();
    } catch (err) { showToast('Błąd: ' + err.message); }
    finally { setSaving(false); }
  }

  async function deleteTable(table) {
    if (!window.confirm(`Usunąć stolik ${table.name || table.number}?`)) return;
    const res = await fetch(`/api/tables/${table.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (!res.ok) { showToast('Błąd: ' + data.error); return; }
    showToast('Usunięto stolik');
    load();
  }

  const STATUS_LABEL = { free: 'Wolny', occupied: 'Zajęty', reserved: 'Rezerwacja' };
  const STATUS_COLOR = { free: 'var(--green)', occupied: 'var(--blue-dark)', reserved: 'var(--amber)' };

  return (
    <div style={{ padding: 24, maxWidth: 700 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 500 }}>Zarządzanie stolikami</h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{tables.length} stolików</p>
        </div>
        <button className="btn-primary" style={{ width: 'auto', padding: '9px 16px' }}
          onClick={() => setEditTable({ number: '', name: '', seats: 2 })}>
          + Nowy stolik
        </button>
      </div>

      <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        {tables.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>Brak stolików</div>
        )}
        {tables.map((table, i) => (
          <div key={table.id} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
            borderBottom: i < tables.length - 1 ? '0.5px solid var(--border)' : 'none',
          }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 14 }}>
              {table.number}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>
                {table.name || `Stolik ${table.number}`}
                {table.name && <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400, marginLeft: 6 }}>nr {table.number}</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{table.seats} os.</div>
            </div>
            <span style={{ fontSize: 12, fontWeight: 500, color: STATUS_COLOR[table.status] }}>
              {STATUS_LABEL[table.status]}
            </span>
            {Number(table.open_total) > 0 && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{Number(table.open_total).toFixed(2)} zł</span>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn-sm" onClick={() => setEditTable({ id: table.id, number: table.number, name: table.name || '', seats: table.seats })}>
                Edytuj
              </button>
              <button className="btn-sm danger" onClick={() => deleteTable(table)}
                disabled={table.status === 'occupied'}>
                Usuń
              </button>
            </div>
          </div>
        ))}
      </div>

      {editTable && (
        <TableModal form={editTable} onSave={saveTable} onClose={() => setEditTable(null)} saving={saving} />
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function TableModal({ form: initial, onSave, onClose, saving }) {
  const [form, setForm] = useState({ ...initial });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isNew = !initial.id;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 360, maxWidth: '90vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500 }}>{isNew ? 'Nowy stolik' : 'Edytuj stolik'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>

        <div className="field">
          <label>Numer stolika</label>
          <input type="number" min="1" value={form.number} onChange={e => set('number', e.target.value)} placeholder="np. 1" />
        </div>
        <div className="field">
          <label>Nazwa własna <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>(opcjonalna)</span></label>
          <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="np. Taras, VIP, Przy oknie" />
        </div>
        <div className="field">
          <label>Liczba miejsc</label>
          <input type="number" min="1" max="20" value={form.seats} onChange={e => set('seats', e.target.value)} placeholder="2" />
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <button className="btn-outline" onClick={onClose} style={{ flex: 1 }}>Anuluj</button>
          <button className="btn-primary" style={{ flex: 2 }} onClick={() => onSave(form)}
            disabled={saving || !form.number || !form.seats}>
            {saving ? 'Zapisywanie...' : 'Zapisz'}
          </button>
        </div>
      </div>
    </div>
  );
}
